/**
 * A section's status as an icon: a shape of its own as well as a color, so it
 * reads without the color, in the chips' tones (status.ts). It carries the
 * status as screen-reader text too, unless a visible label sits beside it
 * (`labelled`). `plain` draws it in the text color, for a chip or a filled
 * card whose background already carries the tone.
 */
import { Circle, CircleAlert, CircleCheck, Eye, LoaderCircle, Lock, TriangleAlert, type LucideIcon } from 'lucide-react'
import type { NodeStatus } from '../../types/transit'
import { cn } from '../../lib/utils'
import { nodeStatusLabel } from './status'

const ICONS: Record<NodeStatus, [LucideIcon, string]> = {
  blocked: [Lock, 'text-muted-foreground/70'],
  ready: [Circle, 'text-brand'],
  drafting: [LoaderCircle, 'text-brand'],
  // Filled: the one that needs an answer from you stands out most.
  needs_input: [CircleAlert, 'fill-warning text-white dark:text-neutral-950'],
  review: [Eye, 'text-link'],
  done: [CircleCheck, 'text-success'],
  failed: [TriangleAlert, 'text-destructive'],
}

export default function StatusIcon({ status, className, plain, labelled }: {
  status: NodeStatus
  className?: string
  plain?: boolean
  labelled?: boolean
}) {
  const [Icon, tone] = ICONS[status]
  return (
    <>
      <Icon aria-hidden strokeWidth={2}
        className={cn('h-3.5 w-3.5 shrink-0', !plain && tone, status === 'drafting' && 'animate-spin motion-reduce:animate-none', className)} />
      {!labelled && <span className="sr-only">{nodeStatusLabel(status)}: </span>}
    </>
  )
}
